import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { getStoredUser, hasUsableRefreshToken, setUnauthorizedHandler } from '@/gateway/client'
import * as authGateway from '@/gateway/modules/auth'
import { AuthContext, type AuthContextValue } from '@/providers/authContext'
import type { AuthUser, LoginCredentials, LoginOutcome } from '@/types/domain/auth'
import { reportError } from '@/utils/reportError'

export function AuthProvider({ children }: { children: ReactNode }) {
  // The stored session carries a snapshot of the user, so a reload restores the
  // signed-in user synchronously — no network call, no flash of the skeleton.
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser())
  const [isRestoring, setIsRestoring] = useState<boolean>(
    () => getStoredUser() === null && hasUsableRefreshToken(),
  )
  const [restoreFailed, setRestoreFailed] = useState(false)

  /**
   * Rebuilds the session after a reload.
   *
   * The normal path is local: the stored session carries a user snapshot, so
   * restoring costs nothing and the rate-limited refresh endpoint (ten calls a
   * minute) is not touched — reloading a page is not a suspicious action. The
   * token pair renews lazily in the gateway's request path when it is actually
   * near expiry.
   *
   * The refresh call remains only as the fallback for a session stored by a
   * build that predates the snapshot (tokens but no user, and the API has no
   * `GET /auth/me` to ask instead). On that path, only an actual refusal signs
   * the user out. A refresh that got no verdict — rate limited, a 5xx, a
   * timeout, offline — leaves the stored refresh token in place and reports
   * `unknown`; treating that as a dead session is what used to bounce a user
   * to /login for pressing F5 eleven times.
   *
   * `restoreFailed` drives the retry banner. There is no automatic retry loop
   * on top of it: the reload that landed here is itself the retry, and hammering
   * a rate limited endpoint is what caused the 429 in the first place.
   */
  const restore = useCallback(async () => {
    const snapshot = getStoredUser()
    if (snapshot !== null) {
      setUser(snapshot)
      setRestoreFailed(false)
      setIsRestoring(false)
      return
    }

    if (!hasUsableRefreshToken()) {
      setUser(null)
      setRestoreFailed(false)
      setIsRestoring(false)
      return
    }

    // Also covers the manual retry from `SessionRestoreFailed`, where the boot
    // attempt has already finished: without this the retry would give no
    // feedback at all until it resolved.
    setRestoreFailed(false)
    setIsRestoring(true)
    try {
      const outcome = await authGateway.refreshSession()

      if (outcome.status === 'renewed') {
        setUser(outcome.session.user)
        return
      }

      // The session is over only on an explicit refusal.
      setUser(null)
      if (outcome.status === 'unknown') setRestoreFailed(true)
    } catch (error) {
      // `refreshSession` does not reject; this is belt-and-braces, and is a
      // failure to establish the session rather than proof it ended.
      setUser(null)
      setRestoreFailed(true)
      reportError(error, { scope: 'auth.restore' })
    } finally {
      setIsRestoring(false)
    }
  }, [])

  useEffect(() => {
    void restore()
  }, [restore])

  /*
   * A reload deliberately does NOT refresh the token pair to re-pull roles and
   * permissions (it used to, once the snapshot was a minute old). The API
   * rotates the refresh token on every use and treats a superseded token as
   * theft, revoking every session the user has. A reload that lands while that
   * refresh is in flight lets the server rotate the token but discards the
   * response, so the browser keeps the superseded one — and the next reload
   * presents it and is signed out everywhere. Repeated F5 made that likely.
   *
   * The snapshot is rewritten on every rotation the request path makes anyway,
   * and the backend enforces the real permissions on every request whatever the
   * UI shows, so the trade is a permission change surfacing on the next
   * rotation instead of the next reload.
   */

  // A 401 from any request drops the session; ProtectedRoute then redirects.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(null)
  }, [])

  const login = useCallback(async (credentials: LoginCredentials): Promise<LoginOutcome> => {
    const outcome = await authGateway.login(credentials)
    if (outcome.status === 'signedIn') {
      setRestoreFailed(false)
      setUser(outcome.session.user)
    }
    return outcome
  }, [])

  const verifyLoginOtp = useCallback(async (challengeId: string, otp: string) => {
    const session = await authGateway.verifyLoginOtp({ challengeId, otp })
    setRestoreFailed(false)
    setUser(session.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authGateway.logout()
    } finally {
      setRestoreFailed(false)
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isRestoring,
      restoreFailed,
      permissions: new Set(user?.permissions ?? []),
      login,
      verifyLoginOtp,
      logout,
      refresh: restore,
    }),
    [user, isRestoring, restoreFailed, login, verifyLoginOtp, logout, restore],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
